<template>
  <!-- Give back line items of a paid invoice with a Stripe credit note, to the card or
       to the customer's credit. The billing engine reads a credited line as gone from
       the week and adds it back on its next run, so an invoice waits for its week to start:
       from that Sunday neither the customer nor the billing engine changes the order.
       A ticked line takes all that is left of it; a quantity or a $ amount takes less. -->
  <v-dialog :value="value" max-width="820px" persistent scrollable @input="$emit('input', $event)">
    <v-card>
      <v-card-title>Refund or credit an invoice</v-card-title>
      <v-card-text style="min-height: 420px">
        <v-alert v-if="error" type="error" dense outlined>{{ error }}</v-alert>
        <v-progress-linear v-if="loading" indeterminate/>

        <div v-if="!loading && !invoices.length" class="text--secondary">
          No paid invoices.
        </div>
        <v-autocomplete
          v-if="invoices.length"
          :value="selected"
          :items="invoices"
          :item-text="invoiceLabel"
          item-value="id"
          return-object
          label="Invoice"
          placeholder="Search by week or invoice"
          outlined
          dense
          hide-details
          data-cy="refundable-invoices"
          @change="select"
        />

        <template v-if="selected">
          <v-progress-linear v-if="loadingLines" indeterminate class="mt-3"/>
          <template v-else-if="detail">
            <div class="caption text--secondary mt-2" data-cy="invoice-paid">
              {{ formatCurrency(selected.total) }} total, {{ formatCurrency(selected.refundable) }} refundable to the card
              <span v-if="selected.refunded">, {{ formatCurrency(selected.refunded) }} already refunded</span>
              <span v-if="selected.account === 'old'"> (old Stripe account)</span>
            </div>
            <div class="subtitle-2 mt-3">Line items</div>
            <v-text-field v-model="lineSearch" label="Filter line items" prepend-inner-icon="mdi-magnify" class="mt-2"
                          clearable outlined dense hide-details data-cy="line-search"/>
            <v-simple-table dense data-cy="invoice-lines">
              <thead>
              <tr>
                <th width="40">
                  <v-simple-checkbox :value="allChosen" :disabled="!openLines.length" color="primary" :ripple="false"
                                     data-cy="all-lines" @input="chooseAll"/>
                </th>
                <th>{{ lineSearch ? 'All shown' : 'All line items' }}</th>
                <th class="text-right">Qty</th>
                <th class="text-right">Amount</th>
                <th width="90">Credit qty</th>
                <th width="110">or amount</th>
              </tr>
              </thead>
              <tbody>
              <tr v-if="!visibleLines.length">
                <td colspan="6" class="text--secondary">No line items match.</td>
              </tr>
              <tr v-for="line in visibleLines" :key="line.id" :class="{'text--disabled': !isOpen(line)}">
                <td>
                  <v-simple-checkbox :value="isChosen(line)" :disabled="!isOpen(line)" color="primary" :ripple="false"
                                     @input="choose(line, $event)"/>
                </td>
                <td>
                  {{ line.description }}
                  <span v-if="line.credited_amount" class="caption">
                    ({{ isOpen(line) ? `${formatCurrency(line.net_left)} left, rest credited` : 'already credited' }})
                  </span>
                </td>
                <td class="text-right">{{ line.quantity }}</td>
                <td class="text-right">{{ formatCurrency(line.net_amount) }}</td>
                <td>
                  <v-text-field v-if="isChosen(line)" :value="chosen[line.id].quantity" type="number" min="1" step="1"
                                dense hide-details single-line data-cy="credit-quantity" @input="setQuantity(line, $event)"/>
                </td>
                <td>
                  <v-text-field v-if="isChosen(line)" :value="chosen[line.id].amount" type="number" min="0" step="0.01" prefix="$"
                                dense hide-details single-line data-cy="credit-amount" @input="setAmount(line, $event)"/>
                </td>
              </tr>
              </tbody>
            </v-simple-table>

            <v-alert v-if="!weekStarted" type="info" dense text class="mt-3" data-cy="week-not-started">
              This invoice can be refunded or credited from Sunday {{ selected.week_date }}. Until then the billing
              engine would add the line items back. Use Adjust on the credit balance to give credit now.
            </v-alert>
            <template v-else>
              <v-radio-group v-model="mode" row dense hide-details class="mt-3">
                <v-radio label="Credit to their balance" value="credit" :disabled="selected.account === 'old'"/>
                <v-radio label="Refund to the card" value="refund" :disabled="!detail.refundable"/>
              </v-radio-group>
              <div class="caption text--secondary" data-cy="give-back-split">{{ splitText }}</div>
              <v-alert v-if="quoteError" type="error" dense text class="mt-2">{{ quoteError }}</v-alert>
              <v-textarea v-model="note" class="mt-2" label="Note (why, required)" rows="2" auto-grow
                          hint="Saved on the Stripe credit note and as a CS note on the customer" persistent-hint/>
            </template>
          </template>
        </template>
      </v-card-text>
      <v-card-actions>
        <v-spacer/>
        <v-btn text :disabled="refunding" @click="$emit('input', false)">Cancel</v-btn>
        <v-btn color="error" :loading="refunding" :disabled="!canRefund" @click="refund">
          {{ mode === 'credit' ? 'Credit' : 'Refund' }} {{ quote ? formatCurrency(quote.amount) : '' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import api from '@/api';
import {dateFormatted, formatCurrency, getDateString} from '@/store/utils';

export default {
  name: 'CustomerRefundDialog',
  props: {
    value: {type: Boolean, default: false},
    uid: {type: String, required: true},
  },
  data() {
    return {
      invoices: [],
      loading: false,
      error: null,
      selected: null,
      detail: null,
      loadingLines: false,
      // line id -> {quantity} or {amount} in dollars; a line starts ticked with all that is left of it
      chosen: {},
      lineSearch: '',
      quote: null,
      quoteError: null,
      quoting: false,
      mode: 'credit',
      note: '',
      refunding: false,
    };
  },
  computed: {
    // by name; a ticked line stays shown, so nothing is given back out of sight
    visibleLines() {
      if (!this.detail) return [];
      const search = (this.lineSearch || '').trim().toLowerCase();
      return this.detail.lines
        .filter(line => line.description.toLowerCase().includes(search) || this.isChosen(line))
        .sort((a, b) => a.description.localeCompare(b.description));
    },
    openLines() {
      return this.visibleLines.filter(this.isOpen);
    },
    allChosen() {
      return !!this.openLines.length && this.openLines.every(line => {
        const [choice, all] = [this.chosen[line.id] || {}, this.allOf(line)];
        return Number(choice.quantity) === Number(all.quantity) && Number(choice.amount) === Number(all.amount);
      });
    },
    chosenLines() {
      return Object.entries(this.chosen).map(([id, {quantity, amount}]) => (
        amount !== '' ? {id, amount: Math.round(Number(amount) * 100)} : {id, quantity: Number(quantity)}
      ));
    },
    splitText() {
      if (!this.chosenLines.length) return 'Tick the line items to give back.';
      if (!this.quote) return this.quoting ? 'Working out the amount...' : '';
      return [
        this.quote.refund_amount ? `${formatCurrency(this.quote.refund_amount)} to the card` : '',
        this.quote.credit_amount ? `${formatCurrency(this.quote.credit_amount)} to their credit` : '',
      ].filter(Boolean).join(', ');
    },
    // week dates are Sundays, YYYY-MM-DD; an invoice without one is not the billing engine's
    weekStarted() {
      const week = this.selected && this.selected.week_date;
      return !week || week <= getDateString(new Date());
    },
    canRefund() {
      return this.weekStarted && !!this.quote && !this.quoting && !!this.note.trim();
    },
  },
  watch: {
    value(open) {
      if (open) this.load();
    },
    chosen: 'quoteSoon',
    mode: 'quoteSoon',
  },
  methods: {
    dateFormatted,
    formatCurrency,

    isOpen(line) {
      return line.left > 0;
    },

    async load() {
      this.loading = true;
      this.error = null;
      this.clearSelection();
      try {
        const {data} = await api.get(`v2/stripe/coupons/customer/${this.uid}/refundable`);
        this.invoices = data.invoices;
      } catch (e) {
        this.error = this.messageOf(e, 'Failed to load the customer\'s paid invoices');
      } finally {
        this.loading = false;
      }
    },

    clearSelection() {
      this.selected = null;
      this.detail = null;
      this.chosen = {};
      this.lineSearch = '';
      this.note = '';
    },

    invoiceLabel(invoice) {
      const week = invoice.week_date || dateFormatted(invoice.created * 1000, {formatString: 'll'});
      return `${week} - ${formatCurrency(invoice.total)} - ${invoice.id}${invoice.account === 'old' ? ' (old account)' : ''}`;
    },

    async select(invoice) {
      if (!invoice || this.selected === invoice) return;
      this.selected = invoice;
      this.detail = null;
      this.chosen = {};
      this.lineSearch = '';
      this.loadingLines = true;
      this.error = null;
      try {
        const {data} = await api.get(`v2/stripe/coupons/customer/${this.uid}/invoice/${invoice.account}/${invoice.id}/lines`);
        if (this.selected !== invoice) return;
        this.detail = data;
        this.mode = invoice.account === 'old' && data.refundable ? 'refund' : 'credit';
      } catch (e) {
        this.error = this.messageOf(e, 'Failed to load the invoice\'s line items');
      } finally {
        this.loadingLines = false;
      }
    },

    isChosen(line) {
      return this.chosen[line.id] !== undefined;
    },

    chooseAll(on) {
      this.chosen = on ? Object.fromEntries(this.openLines.map(line => [line.id, this.allOf(line)])) : {};
    },

    choose(line, on) {
      const {[line.id]: dropped, ...others} = this.chosen; // eslint-disable-line no-unused-vars
      this.chosen = on ? {...others, [line.id]: this.allOf(line)} : others;
    },

    setQuantity(line, quantity) {
      this.chosen = {...this.chosen, [line.id]: {quantity, amount: ''}};
    },

    setAmount(line, amount) {
      this.chosen = {...this.chosen, [line.id]: {quantity: '', amount}};
    },

    // what is left of a line: its quantity, or its dollars once part of it was credited by amount
    allOf(line) {
      return Number.isInteger(line.left_quantity)
        ? {quantity: line.left_quantity, amount: ''}
        : {quantity: '', amount: (line.net_left / 100).toFixed(2)};
    },

    // the server rounds as Stripe will; typing in a quantity asks once it settles
    quoteSoon() {
      this.quote = null;
      this.quoteError = null;
      clearTimeout(this.quoteTimer);
      if (!this.chosenLines.length) return;
      this.quoting = true;
      this.quoteTimer = setTimeout(this.fetchQuote, 300);
    },

    async fetchQuote() {
      const chosen = this.chosen;
      try {
        const {data} = await api.post(`v2/stripe/coupons/customer/${this.uid}/refund/preview`, this.request());
        if (chosen === this.chosen) this.quote = data;
      } catch (e) {
        if (chosen === this.chosen) this.quoteError = this.messageOf(e, 'Could not work out the amount');
      } finally {
        if (chosen === this.chosen) this.quoting = false;
      }
    },

    request() {
      return {invoiceId: this.selected.id, account: this.selected.account, lines: this.chosenLines, mode: this.mode};
    },

    async refund() {
      this.refunding = true;
      this.error = null;
      try {
        const {data} = await api.post(`v2/stripe/coupons/customer/${this.uid}/refund`, {
          ...this.request(),
          note: this.note.trim(),
        });
        this.$emit('refunded');
        this.$store.commit('showSnackbar', {message: `${formatCurrency(data.credit_note.amount)} given back: ${this.splitText}`, color: 'success'});
        this.$emit('input', false);
      } catch (e) {
        this.error = this.messageOf(e, 'Failed to give the money back');
      } finally {
        this.refunding = false;
      }
    },

    messageOf(e, fallback) {
      return (e.response && e.response.data && (e.response.data.message || e.response.data.error)) || e.message || fallback;
    },
  },
};
</script>
